import { Component, EventEmitter, inject, Input, OnDestroy, Output } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FileErrorModalComponent } from "@components/file-error-modal/file-error-modal.component";
import { ImageUploadModalComponent } from "@components/image-upload-modal/image-upload-modal.component";
import { CameraModalComponent } from "@components/image-upload-overlay/camera-modal/camera-modal.component";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { ProjectImageData, ProjectImageService, SyncState } from "@domain/project/project-image.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ModalService } from "@odx/angular/components/modal";
import { filter, forkJoin, map, Observable, Subscription, switchMap } from "rxjs";

@Component({
  selector: "app-image-upload-overlay",
  templateUrl: "./image-upload-overlay.component.html",
  styleUrls: ["./image-upload-overlay.component.scss"],
  imports: [UiKitModule],
  standalone: true,
})
export class ImageUploadOverlayComponent implements OnDestroy {
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private projectImageService = inject(ProjectImageService);

  private readonly modalService = inject(ModalService);
  protected readonly BusinessScope = BusinessScope;

  @Output() image$ = new EventEmitter<ProjectImageData[]>();
  @Input() useProjectPhotosMobileModals = false;
  @Input() projectId!: string;

  createImagesSubscriber: Subscription[] = [];
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  businessScope: BusinessScope | undefined;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );

    this.userService.businessScope$.subscribe((scope) => {
      this.businessScope = scope;
    });
  }

  ngOnDestroy(): void {
    this.createImagesSubscriber.forEach((subsciption) => subsciption.unsubscribe());
  }

  onImageSelected(event: any) {
    const invalidFiles = this.projectImageService.checkIfTypeIsWhitelisted(event.target.files);

    if (invalidFiles.length > 0) {
      const unsupportedFiles = this.projectImageService.filterUnsupportedFileTypes(invalidFiles);
      const completeErrorMessage = $localize`:@@modal.imageUpload.invalidFiles.errorMessage:Das Dateiformat von ${unsupportedFiles} wird nicht unterstützt.`;
      this.openUploadErrorModal(completeErrorMessage, event);
      return;
    }
    this.createImagesSubscriber.push(
      this.projectImageService.convertHeicImages(event.target.files).subscribe((images) => {
        event.target.value = null; // must be set to be able to upload the same file multiple times

        if (this.businessScope === BusinessScope.FGDS) {
          const projectImages$ = images.map((image) =>
            this.projectImageService.createPreviewImage(image).pipe(
              map((previewBlob) => ({
                blob: image,
                previewBlob: previewBlob,
                imageMetadata: ProjectImageMetadata.create(image.name),
                projectId: this.projectId,
                syncState: SyncState.NEW,
              })),
            ),
          );
          forkJoin(projectImages$).subscribe((projectImages) => {
            this.image$.emit(projectImages);
          });
        }

        if (this.businessScope === BusinessScope.MONITORING) {
          const uploadImageModalRef = this.modalService.open(ImageUploadModalComponent, {
            id: this.getProjectPhotosModalId("project-photos-upload-modal"),
            data: {
              imageUrl: URL.createObjectURL(images[0]),
              name: images[0].name,
            },
          });

          uploadImageModalRef.onClose$
            .pipe(
              switchMap((result) =>
                this.projectImageService.createPreviewImage(images[0]).pipe(
                  map((previewBlob) => ({
                    blob: images[0],
                    previewBlob: previewBlob,
                    imageMetadata: ProjectImageMetadata.create(images[0].name, result.description, result.tags ?? undefined),
                    projectId: this.projectId,
                    syncState: SyncState.NEW,
                  })),
                ),
              ),
            )
            .subscribe((image) => {
              this.image$.emit([image]);
            });
        }
      }),
    );
  }

  openCamera() {
    const modalRef = this.modalService.open(CameraModalComponent, {
      id: this.getProjectPhotosModalId("project-photos-camera-modal"),
    });
    modalRef.onClose$.subscribe((imageFile: File) => {
      this.createImagesSubscriber.push(
        this.projectImageService.convertHeicImages([imageFile]).subscribe((images) => {
          if (this.businessScope === BusinessScope.FGDS) {
            const projectImages$ = images.map((image) =>
              this.projectImageService.createPreviewImage(image).pipe(
                map((previewBlob) => ({
                  blob: image,
                  previewBlob: previewBlob,
                  imageMetadata: ProjectImageMetadata.create(image.name),
                  projectId: this.projectId,
                  syncState: SyncState.NEW,
                })),
              ),
            );
            forkJoin(projectImages$).subscribe((projectImages) => {
              this.image$.emit(projectImages);
            });
          }

          if (this.businessScope === BusinessScope.MONITORING) {
            const uploadImageModalRef = this.modalService.open(ImageUploadModalComponent, {
              id: this.getProjectPhotosModalId("project-photos-upload-modal"),
              data: {
                imageUrl: URL.createObjectURL(images[0]),
                name: images[0].name,
              },
            });

            uploadImageModalRef.onClose$
              .pipe(
                switchMap((result) =>
                  this.projectImageService.createPreviewImage(images[0]).pipe(
                    map((previewBlob) => ({
                      blob: images[0],
                      previewBlob: previewBlob,
                      imageMetadata: ProjectImageMetadata.create(images[0].name, result.description, result.tags ?? undefined),
                      projectId: this.projectId,
                      syncState: SyncState.NEW,
                    })),
                  ),
                ),
              )
              .subscribe((image) => {
                this.image$.emit([image]);
              });
          }
        }),
      );
    });
  }

  private getProjectPhotosModalId(id: string) {
    return this.useProjectPhotosMobileModals ? id : undefined;
  }

  private openUploadErrorModal(errorMessage: string, event: any) {
    this.modalService
      .open(FileErrorModalComponent, {
        data: {
          text: errorMessage,
        },
      })
      .onClose$.subscribe(() => {
        event.target.value = null;
      });
  }
}
